import { Accounting } from '@/pages/Accounting'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { Layout } from '@/app/Layout'
import { StepUpHost } from '@/components/StepUpHost'
import { PageSkeleton } from '@/components/ui/ShellSkeleton'
import { useAuth } from '@/lib/auth'
import { Activity } from '@/pages/Activity'
import { Login } from '@/pages/Login'
import { NotFound } from '@/pages/NotFound'

// Lazy-load authed pages so the login screen and first paint don't ship the charts bundle.
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Jobs = lazy(() => import('@/pages/Jobs').then((m) => ({ default: m.Jobs })))
const Users = lazy(() => import('@/pages/Users').then((m) => ({ default: m.Users })))
const Pricing = lazy(() => import('@/pages/Pricing').then((m) => ({ default: m.Pricing })))
const Models = lazy(() => import('@/pages/Models').then((m) => ({ default: m.Models })))
const Finance = lazy(() => import('@/pages/Finance').then((m) => ({ default: m.Finance })))
const Payouts = lazy(() => import('@/pages/Payouts').then((m) => ({ default: m.Payouts })))
const Promo = lazy(() => import('@/pages/Promo').then((m) => ({ default: m.Promo })))
const Broadcasts = lazy(() => import('@/pages/Broadcasts').then((m) => ({ default: m.Broadcasts })))
const Content = lazy(() => import('@/pages/Content').then((m) => ({ default: m.Content })))
const Contacts = lazy(() => import('@/pages/Contacts').then((m) => ({ default: m.Contacts })))
const Support = lazy(() => import('@/pages/Support').then((m) => ({ default: m.Support })))
const Audit = lazy(() => import('@/pages/Audit').then((m) => ({ default: m.Audit })))
const System = lazy(() => import('@/pages/System').then((m) => ({ default: m.System })))
const Admins = lazy(() => import('@/pages/Admins').then((m) => ({ default: m.Admins })))
const Growth = lazy(() => import('@/pages/Growth').then((m) => ({ default: m.Growth })))
const Webhooks = lazy(() => import('@/pages/Webhooks').then((m) => ({ default: m.Webhooks })))
const Acquisition = lazy(() => import('@/pages/Acquisition').then((m) => ({ default: m.Acquisition })))

function Protected({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  return token ? <>{children}</> : <Navigate to="/login" replace />
}


export function App() {
  return (
    <>
      {/* One step-up 2FA dialog for every dangerous action (lib/api.ts asks it for a code). */}
      <StepUpHost />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <Protected>
              <Layout />
            </Protected>
          }
        >
          <Route
            element={
              <Suspense fallback={<PageSkeleton />}>
                <RouteOutlet />
              </Suspense>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="users" element={<Users />} />
            <Route path="activity" element={<Activity />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="models" element={<Models />} />
            <Route path="finance" element={<Finance />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="accounting" element={<Accounting />} />
            <Route path="acquisition" element={<Acquisition />} />
            <Route path="webhooks" element={<Webhooks />} />
            <Route path="promo" element={<Promo />} />
            <Route path="broadcasts" element={<Broadcasts />} />
            <Route path="content" element={<Content />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="support" element={<Support />} />
            <Route path="audit" element={<Audit />} />
            <Route path="system" element={<System />} />
            <Route path="admins" element={<Admins />} />
            <Route path="growth" element={<Growth />} />
            {/* An unknown address says so inside the shell (WP-05) instead of opening the
                dashboard; signed-out visitors are sent to /login by Protected first. */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
      </Routes>
    </>
  )
}

// A pass-through outlet so the Suspense boundary wraps every lazy page.
function RouteOutlet() {
  return <Outlet />
}
